import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Paper,
  Typography,
  TextField,
  Chip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CloseIcon from "@mui/icons-material/Close";
import "./compose-sms.scss";
import { AlertComponent } from "../../../../../../common/components";
import { sendCampaignSMS } from "../../../../../../api/campaign";
import { useSetLoading } from "../../../../../../common/hooks/appearance";

const MAX_SMS_LENGTH = 150;
const MAX_RECIPIENTS = 50;

interface Props {
  open: boolean;
  onClose: () => void;
  leadDetails?: any;
  campaignDetails?: any;
  onSuccess?: () => void;
}

const ComposeSMSDialog: React.FC<Props> = ({
  open,
  onClose,
  campaignDetails,
  onSuccess,
}) => {
  const [selectedRecipients, setSelectedRecipients] = useState<any[]>([]);
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [message, setMessage] = useState("");
  const [messageError, setMessageError] = useState("");
  const [loading, setLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [recipientsOptions, setRecipientsOptions] = useState<any[]>([]);

  useEffect(() => {
    if (!open) return;
    const leadAssociations = campaignDetails?.CampaignLeadAssociation ?? [];
    const customerAssociations =
      campaignDetails?.CampaignCustomerAssociation ?? [];
    const eligibleLeads = leadAssociations.filter(
      (item: any) => item?.lead?.primary_phone_number,
    );
    const eligibleCustomers = customerAssociations.filter(
      (item: any) => item?.customer?.primary_phone_number,
    );
    const leadDetails = eligibleLeads?.map((item: any) => ({
      id: item?.lead?.id,
      email: item?.lead?.primary_email_address,
      name: [
        item?.lead?.first_name,
        item?.lead?.middle_name,
        item?.lead?.last_name,
      ]
        .filter(Boolean)
        .join(" "),
      phone: item?.lead?.primary_phone_number,
    }));
    const customerDetails = eligibleCustomers?.map((item: any) => ({
      id: item?.customer?.id,
      email: item?.customer?.primary_email_address,
      name: [
        item?.customer?.first_name,
        item?.customer?.middle_name,
        item?.customer?.last_name,
      ]
        .filter(Boolean)
        .join(" "),
      phone: item?.customer?.primary_phone_number,
    }));
    const allRecipients = [...leadDetails, ...customerDetails];
    setRecipientsOptions(allRecipients);
    setSelectedRecipients([...allRecipients]);
  }, [campaignDetails, open]);

  const handleReset = () => {
    setPhoneInput("");
    setPhoneError("");
    setMessage("");
    setMessageError("");
  };

  // Only remove spaces and dashes, keep '+' and digits
  const normalizePhone = (value: string) => value.trim().replace(/[\s-]/g, "");

  const isValidPhone = (value: string) => {
    return /^\+\d{10,15}$/.test(value.replace(/[\s-]/g, ""));
  };

  // const buildRecipientList = (entries: string[]) => {
  //   const recipients: string[] = [];
  //   const issues: string[] = [];
  //   const seen = new Set<string>();

  //   for (const rawEntry of entries) {
  //     const entry = normalizePhone(rawEntry);
  //     if (!entry) continue;

  //     const key = entry.toLowerCase();
  //     if (seen.has(key)) continue;

  //     if (recipients.length >= MAX_RECIPIENTS) {
  //       issues.push(`Maximum ${MAX_RECIPIENTS} recipients allowed`);
  //       break;
  //     }

  //     seen.add(key);
  //     recipients.push(entry);

  //     if (!isValidPhone(entry)) {
  //       issues.push(`${entry} is invalid (use 10 digits or +1 format)`);
  //     }
  //   }

  //   return { recipients, issues };
  // };

  const addRecipient = (rawInput: string) => {
    const newEntries = rawInput
      .split(/[;,]+/)
      .map((v) => v.trim())
      .filter(Boolean)
      .map((name) => {
        // Try to find in options, else create a new object
        return (
          recipientsOptions.find((opt) => opt.name === name) || {
            id: undefined,
            email: "",
            name,
            phone: name, // fallback if user enters phone directly
          }
        );
      });
    if (newEntries.length === 0) return;
    // Remove duplicates by name
    const unique = [
      ...selectedRecipients,
      ...newEntries.filter(
        (entry) => !selectedRecipients.some((sel) => sel.name === entry.name),
      ),
    ];
    setSelectedRecipients(unique);
    setPhoneInput("");
  };

  // const handleRecipientsChange = (values: string[]) => {
  //   setSelectedNames(values);
  //   setPhoneError("");
  // };

  const handleSend = async () => {
    if (selectedRecipients.length === 0) {
      setPhoneError("At least one recipient is required");
      return;
    }
    const formattedRecipients = selectedRecipients.map((r) => ({
      ...r,
      phone: normalizePhone(r.phone),
    }));
    const invalidRecipients = formattedRecipients.filter(
      (r) => !isValidPhone(r.phone),
    );
    if (invalidRecipients.length > 0) {
      setPhoneError(
        `Invalid recipients (use valid phone format): ${invalidRecipients
          .map((r) => r.name)
          .join(", ")}`,
      );
      return;
    }
    if (formattedRecipients.length > MAX_RECIPIENTS) {
      setPhoneError(`Maximum ${MAX_RECIPIENTS} unique recipients allowed`);
      return;
    }
    if (!message.trim()) {
      setMessageError("Message is required");
      return;
    }
    setPhoneError("");
    setMessageError("");
    const payload = {
      contactValue: formattedRecipients,
      message,
      contactType: "SMS",
    };
    setLoading(true);
    try {
      await sendCampaignSMS(campaignDetails?.id, payload);
      handleReset();
      if (onSuccess) onSuccess();
    } catch (error: any) {
      setAlertMessage(error?.response?.data?.message ?? "Failed to send SMS.");
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const hasInvalidPhones = selectedRecipients.some(
    (r) => !isValidPhone(r.phone),
  );

  if (!open) return null;

  const charCount = message.length;
  const segmentCount = Math.ceil(charCount / MAX_SMS_LENGTH) || 1;
  const charsInCurrentSegment = charCount % MAX_SMS_LENGTH;
  const remaining =
    charsInCurrentSegment === 0 && charCount > 0
      ? 0
      : MAX_SMS_LENGTH - charsInCurrentSegment;
  return (
    <div className="compose-email-dialog">
      <Paper
        elevation={5}
        sx={{
          position: "fixed",
          bottom: 20,
          right: 16,
          width: 480,
          maxHeight: { xs: "460px", sm: "480px", lg: "500px" },
          borderRadius: 2,
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
        className="compose-email-popup-paper"
      >
        <Box
          sx={{
            backgroundColor: "#fff8e1",
            padding: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography fontWeight="bold">New SMS</Typography>
          <IconButton
            size="small"
            onClick={() => {
              handleReset();
              onClose();
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 2 }}>
          {/* Recipients */}
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              To
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              fullWidth
              options={recipientsOptions}
              getOptionLabel={(option) => option.name || option.phone || ""}
              disableClearable
              filterOptions={(options) => options}
              value={selectedRecipients}
              inputValue={phoneInput}
              onInputChange={(_, val) => {
                setPhoneInput(val);
                if (phoneError) setPhoneError("");
              }}
              onChange={(_, newValue) => {
                setSelectedRecipients(newValue as any[]);
                setPhoneError("");
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.name || option.phone}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={""}
                  error={!!phoneError}
                  helperText={phoneError}
                  onBlur={() => {
                    if (phoneInput.trim()) {
                      addRecipient(phoneInput);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (["Enter", ",", ";"].includes(e.key)) {
                      e.preventDefault();
                      addRecipient(phoneInput);
                    }
                  }}
                />
              )}
            />
          </Box>

          {/* Message */}
          <Box>
            <TextField
              fullWidth
              placeholder="Type Message..."
              multiline
              rows={8}
              variant="outlined"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (messageError) setMessageError("");
              }}
              error={!!messageError}
              helperText={messageError}
              inputProps={{ maxLength: MAX_SMS_LENGTH }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", textAlign: "right", mt: 0.5 }}
            >
              {remaining} characters remaining &bull; {segmentCount}{" "}
              {segmentCount === 1 ? "segment" : "segments"}
            </Typography>
          </Box>

          {/* Actions */}
          <Box className="actions-container">
            <Box />
            <Box className="button-actions">
              <button
                className="cancel-button"
                onClick={() => {
                  handleReset();
                  onClose();
                }}
              >
                Cancel
              </button>
              <button
                className="cancel-button save-button"
                onClick={handleSend}
                disabled={
                  selectedRecipients.length === 0 ||
                  hasInvalidPhones ||
                  !!phoneError ||
                  !!messageError
                }
              >
                Send
              </button>
            </Box>
          </Box>
        </Box>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default ComposeSMSDialog;
